import {
    CoreGridInput,
    CoreOrder,
    CoreOrderExpeditionPackage,
    CoreOrderPayload,
    CoreOrdersGridOutput,
    fromCoreOrderNames,
    GridInput,
    GridOutput,
    HTTP_STATUS,
    Order,
    OrderProduct,
    PackingList,
    SkladonError,
    toCoreOrderNames,
    TrackingUrl,
} from 'skladon-types';

import { CoreGridMapper } from './CoreGridMapper';
import { toCoreNumber, toNumber } from './numbers';
import { getTrackingUrl } from '../utils/getTrackingUrl';

const PACKING_LIST_DELIMITER = '|';

export class OrdersMapper {
    private static toTrackingUrls(shipper: string, trackingNumbers: string | null): TrackingUrl[] {
        if (!trackingNumbers) {
            return [];
        }

        return trackingNumbers
            .split(',')
            .map((tn) => tn.trim())
            .filter(Boolean)
            .map((trackingNumber) => ({
                trackingNumber,
                url: getTrackingUrl(shipper, trackingNumber),
            }));
    }

    public static fromCore(coreOrder: CoreOrder): Order {
        return {
            id: coreOrder.ID,
            addressId: coreOrder.ADDRESS_ID,
            allowExpedition: !!coreOrder.ALLOW_EXPEDITION,
            printReturnLabel: !!coreOrder.PRINT_RETURN_LABEL,
            underSanction: !!coreOrder.UNDER_SANCTION,
            billingAddressAdditionalInfo: coreOrder.BILLING_ADDRESS_ADDITIONAL_INFO,
            billingAddressAdditionalInfo2: null,
            billingAttentionName: coreOrder.BILLING_ATTENTION_NAME,
            billingCity: coreOrder.BILLING_CITY,
            billingCompanyId: coreOrder.BILLING_COMPANY_ID,
            billingCompanyName: coreOrder.BILLING_COMPANY_NAME,
            billingEmail: coreOrder.BILLING_EMAIL,
            billingName: coreOrder.BILLING_NAME,
            billingPhone: coreOrder.BILLING_PHONE,
            billingPostCode: coreOrder.BILLING_POST_CODE,
            billingProvinceCode: coreOrder.BILLING_PROVINCE_CODE,
            billingState: coreOrder.BILLING_STATE,
            billingStreet: coreOrder.BILLING_STREET,
            billingTaxId: coreOrder.BILLING_TAX_ID,
            billingGln: coreOrder.BILLING_GLN,
            codCurrency: coreOrder.COD_CURRENCY,
            codPrice: coreOrder.COD_PRICE,
            codRefNumber: coreOrder.COD_REF_NUMBER,
            currency: coreOrder.CURRENCY,
            eshopOrderId: coreOrder.ESHOP_ORDER_ID,
            invoiceDate: coreOrder.INVOICE_DATE,
            requiredExpeditionDate: coreOrder.REQUIRED_EXPEDITION_DATE
                ? String(coreOrder.REQUIRED_EXPEDITION_DATE)
                : null,
            invoiceNumber: coreOrder.INVOICE_NUMBER,
            marketplace: coreOrder.MARKETPLACE || null,
            message: coreOrder.MESSAGE,
            note: coreOrder.NOTE,
            orderProducts: coreOrder.ORDER_PRODUCTS
                ? coreOrder.ORDER_PRODUCTS.map((product): NonNullable<Order['orderProducts']>[number] => ({
                      count: product.COUNT,
                      productPrice: product.PRODUCT_PRICE,
                      lot: product.LOT || null,
                      locationId: null,
                      sku: product.PRODUCT!.SKU,
                      customsProductType: product.CUSTOMS_PRODUCT_TYPE,
                      lineState: product.LINE_STATE || 'E',
                      serialNumbers: product.SERIAL_NUMBER
                          ? OrdersMapper.splitSerialNumber(product.SERIAL_NUMBER).map((sn: string) => sn.trim())
                          : [],
                      bcxCodes: product.BCX_CODE ? product.BCX_CODE.split(',') : undefined,
                      confirmed: product.CONFIRMED || null,
                      missingCount: product.MISSING_COUNT || null,
                      customizationType: product.CUSTOMIZATION_TYPE,
                      customizationText: product.CUSTOMIZATION_TEXT,
                      eshopOrderProductId: product.ESHOP_ORDER_PRODUCT_ID,
                      orderProductFiles: product.ORDER_PRODUCT_FILES
                          ? product.ORDER_PRODUCT_FILES.map((file) => ({
                                id: file.FILE_ID,
                                type: file.FILE.TYPE,
                                fileType: file.FILE.FILE_TYPE,
                                fileName: file.FILE.ORIGINAL_FILE_NAME,
                                fileSize: file.FILE.FILE_SIZE,
                                createdAt: file.FILE.CREATED_AT ?? null,
                                creatorRole: file.FILE.CREATOR_ROLE ?? null
                            }))
                          : []
                  }))
                : undefined,
            orderFiles: coreOrder.ORDER_FILES
                ? coreOrder.ORDER_FILES.map((file) => ({
                      id: file.FILE_ID,
                      type: file.FILE.TYPE,
                      fileType: file.FILE.FILE_TYPE,
                      fileName: file.FILE.ORIGINAL_FILE_NAME,
                      fileSize: file.FILE.FILE_SIZE,
                      createdAt: file.FILE.CREATED_AT ?? null,
                      creatorRole: file.FILE.CREATOR_ROLE ?? null
                  }))
                : undefined,
            orderType: coreOrder.ORDER_TYPE,
            paid: !!coreOrder.PAID,
            partnerOrderId: coreOrder.PARTNER_ORDER_ID,
            paymentMethod: coreOrder.PAYMENT_METHOD,
            price: coreOrder.PRICE,
            printFiles: !!coreOrder.PRINT_FILES,
            holdByQuality: !!coreOrder.HOLD_BY_QUALITY,
            pickFromCustomsWarehouse: !!coreOrder.PICK_FROM_CUSTOMS_WAREHOUSE,
            recipientAddressAdditionalInfo: coreOrder.RECIPIENT_ADDRESS_ADDITIONAL_INFO,
            recipientAddressAdditionalInfo2: null,
            recipientAttentionName: coreOrder.RECIPIENT_ATTENTION_NAME,
            recipientCity: coreOrder.RECIPIENT_CITY,
            recipientCompanyId: coreOrder.RECIPIENT_COMPANY_ID,
            recipientCompanyName: coreOrder.RECIPIENT_COMPANY_NAME,
            recipientEmail: coreOrder.RECIPIENT_EMAIL,
            recipientName: coreOrder.RECIPIENT_NAME,
            recipientPhone: coreOrder.RECIPIENT_PHONE,
            recipientPostCode: coreOrder.RECIPIENT_POST_CODE,
            recipientProvinceCode: coreOrder.RECIPIENT_PROVINCE_CODE,
            recipientState: coreOrder.RECIPIENT_STATE,
            recipientStreet: coreOrder.RECIPIENT_STREET,
            recipientTaxId: coreOrder.RECIPIENT_TAX_ID,
            recipientGln: coreOrder.RECIPIENT_GLN,
            shipper: coreOrder.SHIPPER,
            shipperServiceCode: coreOrder.SHIPPER_SERVICE_CODE,
            branchId: coreOrder.BRANCH_ID,
            shippingPrice: coreOrder.SHIPPING_PRICE,
            status: coreOrder.STATUS,
            timestamp: coreOrder.TIMESTAMP,
            deliveredAt: coreOrder.DELIVERED_AT ? String(coreOrder.DELIVERED_AT) : null,
            trackingNumbers: coreOrder.TRACKING_NUMBER
                ? coreOrder.TRACKING_NUMBER.split(',').map((tn) => tn.trim())
                : [],
            trackingUrls: OrdersMapper.toTrackingUrls(coreOrder.SHIPPER, coreOrder.TRACKING_NUMBER),
            returnTrackingUrls: OrdersMapper.toTrackingUrls(
                coreOrder.SHIPPER,
                coreOrder.RETURN_TRACKING_NUMBER
            ),
            exchangeTrackingUrl: coreOrder.EXCHANGE_TRACKING_NUMBER
                ? getTrackingUrl(coreOrder.SHIPPER, coreOrder.EXCHANGE_TRACKING_NUMBER)
                : null,
            updatedAt: coreOrder.UPDATED_AT ? String(coreOrder.UPDATED_AT) : '',
            disableShippingReorder: !!coreOrder.DISABLE_SHIPPING_REORDER,
            waitingForPaymentAt: coreOrder.WAITING_FOR_PAYMENT_AT ? String(coreOrder.WAITING_FOR_PAYMENT_AT) : null,
            productsNotAvailableAt: coreOrder.PRODUCTS_NOT_AVAILABLE_AT
                ? String(coreOrder.PRODUCTS_NOT_AVAILABLE_AT)
                : null,
            suspendedAt: coreOrder.SUSPENDED_AT ? String(coreOrder.SUSPENDED_AT) : null,
            releaseFromSuspendedAt: coreOrder.RELEASE_FROM_SUSPENDED_AT
                ? String(coreOrder.RELEASE_FROM_SUSPENDED_AT)
                : null,
            preorderReleaseAt: coreOrder.PREORDER_RELEASE_AT
                ? String(coreOrder.PREORDER_RELEASE_AT)
                : null,
            incorrectAt: coreOrder.INCORRECT_AT ? String(coreOrder.INCORRECT_AT) : null,
            waitingForShipmentAt: coreOrder.WAITING_FOR_SHIPMENT_AT ? String(coreOrder.WAITING_FOR_SHIPMENT_AT) : null,
            shipmentOrderedAt: coreOrder.SHIPMENT_ORDERED_AT ? String(coreOrder.SHIPMENT_ORDERED_AT) : null,
            inProcessAt: coreOrder.IN_PROCESS_AT ? String(coreOrder.IN_PROCESS_AT) : null,
            packedAt: coreOrder.PACKED_AT ? String(coreOrder.PACKED_AT) : null,
            waitingForCarrierAt: coreOrder.WAITING_FOR_CARRIER_AT ? String(coreOrder.WAITING_FOR_CARRIER_AT) : null,
            shippedAt: coreOrder.SHIPPED_AT ? String(coreOrder.SHIPPED_AT) : null,
            atPickupPointAt: coreOrder.AT_PICKUP_POINT_AT ? String(coreOrder.AT_PICKUP_POINT_AT) : null,
            returningBackAt: coreOrder.RETURNING_BACK_AT ? String(coreOrder.RETURNING_BACK_AT) : null,
            returnedBackAt: coreOrder.RETURNED_BACK_AT ? String(coreOrder.RETURNED_BACK_AT) : null,
            preReceivedAt: coreOrder.PRERECEIVED_AT ? String(coreOrder.PRERECEIVED_AT) : null,
            canceledAt: coreOrder.CANCELED_AT ? String(coreOrder.CANCELED_AT) : null,
            slaDate: coreOrder.SLA_DATE ? String(coreOrder.SLA_DATE) : null,
            scannedAt: coreOrder.SCANNED_AT ? String(coreOrder.SCANNED_AT) : null,
            sourceSys: coreOrder.SOURCE_SYS ?? null,
            weight: toNumber(coreOrder.WEIGHT),
            parentOrderId: coreOrder.PARENT_ORDER_ID,
            parentEshopOrderId: coreOrder.PARENT_ESHOP_ORDER_ID,
            returnTrackingNumber: coreOrder.RETURN_TRACKING_NUMBER,
            exchangeTrackingNumber: coreOrder.EXCHANGE_TRACKING_NUMBER,
            eshopNote: coreOrder.ESHOP_NOTE,
            orderBoxCount: coreOrder.ORDER_BOX_COUNT,
            incorrectOrderMessage: coreOrder.INCORRECT_ORDER_MESSAGE
        };
    }

    public static splitSerialNumber(serialNumber: string): string[] {
        const splits = serialNumber.split(';');
        return splits.map((tn) => tn.trim());
    }

    public static getPackingListsBySku(sku: string, packages: CoreOrderExpeditionPackage[]): PackingList[] {
        return packages.reduce((packingLists: PackingList[], expeditionPackage) => {
            const imeis: string[] = expeditionPackage.SERIAL_NUMBERS?.split(PACKING_LIST_DELIMITER) || [];
            const skus: string[] = expeditionPackage.SKUS?.split(PACKING_LIST_DELIMITER) || [];
            const counts: string[] = expeditionPackage.COUNTS?.split(PACKING_LIST_DELIMITER) || [];
            const lots: string[] = expeditionPackage.LOTS?.split(PACKING_LIST_DELIMITER) || [];
            const expDates: string[] = expeditionPackage.EXP_DATES?.split(PACKING_LIST_DELIMITER) || [];
            // this packages contains SKU
            if (skus.includes(sku)) {
                skus.forEach((packageSku, index) => {
                    if (packageSku === sku) {
                        const serialNumbers = imeis[index] ? imeis[index].trim() : '';
                        const lot = lots[index] ? lots[index].trim() : '';
                        const expDate = expDates[index] ? expDates[index].trim() : '';
                        const ssccCode =
                            expeditionPackage.SSCC_NUMBER ||
                            packages.find((pack) => pack.EXP_PCKG === expeditionPackage.EXP_PAL)?.SSCC_NUMBER ||
                            '';
                        const existingPackingList = packingLists.find((pl) => pl.id === expeditionPackage.ID);
                        if (existingPackingList) {
                            existingPackingList.count += Number(counts[index]);
                            if (serialNumbers) {
                                existingPackingList.serialNumbers = [
                                    ...existingPackingList.serialNumbers,
                                    ...OrdersMapper.splitSerialNumber(serialNumbers),
                                ];
                            }
                            if (lot) {
                                existingPackingList.lots = [
                                    ...(existingPackingList.lots || []),
                                    ...OrdersMapper.splitSerialNumber(lot),
                                ];
                            }
                            if (expDate) {
                                existingPackingList.expDates = [
                                    ...(existingPackingList.expDates || []),
                                    ...OrdersMapper.splitSerialNumber(expDate),
                                ];
                            }
                        } else {
                            packingLists.push({
                                id: expeditionPackage.ID,
                                expeditionPalletNumber:
                                    expeditionPackage.EXP_PAL === null || expeditionPackage.EXP_PAL === undefined
                                        ? null
                                        : String(expeditionPackage.EXP_PAL),
                                expeditionBoxNumber: String(expeditionPackage.EXP_PCKG),
                                ssccCode: String(ssccCode),
                                serialNumbers: serialNumbers ? OrdersMapper.splitSerialNumber(serialNumbers) : [],
                                lots: lot ? OrdersMapper.splitSerialNumber(lot) : [],
                                expDates: expDate ? OrdersMapper.splitSerialNumber(expDate) : [],
                                count: Number(counts[index]),
                            });
                        }
                    }
                });
            }
            return packingLists;
        }, []);
    }

    public static addPackingListsToOrderProducts(
        orderProducts: OrderProduct[],
        orderPackages: CoreOrderExpeditionPackage[]
    ): OrderProduct[] {
        return orderProducts.map((orderProduct) => ({
            ...orderProduct,
            packingLists: OrdersMapper.getPackingListsBySku(orderProduct.sku, orderPackages),
        }));
    }

    public static addPackingListsToOrder<T extends Pick<Order, 'orderProducts'>>(
        order: T,
        orderPackages: CoreOrderExpeditionPackage[]
    ): T {
        if (!order.orderProducts) {
            return order;
        }

        return {
            ...order,
            orderProducts: OrdersMapper.addPackingListsToOrderProducts(order.orderProducts, orderPackages),
        };
    }

    public static toCore(order: Partial<Order>): CoreOrderPayload {
        order.orderFiles?.forEach((file) => {
            if (file.type === 'shipResponse' && (!file.id || file.data)) {
                throw new SkladonError(
                    HTTP_STATUS.BAD_REQUEST,
                    `You cannot send shipResponse file only with id and without data!`
                );
            }
            if (file.id && file.data) {
                throw new SkladonError(HTTP_STATUS.BAD_REQUEST, `You cannot send file with both id and data!`);
            }
        });
        return {
            ADDRESS_ID: order.addressId,
            ALLOW_EXPEDITION: order.allowExpedition !== undefined ? Number(order.allowExpedition) : undefined,
            PRINT_RETURN_LABEL: order.printReturnLabel !== undefined ? Number(order.printReturnLabel) : undefined,
            BILLING_ADDRESS_ADDITIONAL_INFO: order.billingAddressAdditionalInfo,
            BILLING_ATTENTION_NAME: order.billingAttentionName,
            BILLING_CITY: order.billingCity,
            BILLING_COMPANY_ID: order.billingCompanyId,
            BILLING_COMPANY_NAME: order.billingCompanyName,
            BILLING_EMAIL: order.billingEmail,
            BILLING_NAME: order.billingName,
            BILLING_PHONE: order.billingPhone,
            BILLING_POST_CODE: order.billingPostCode,
            BILLING_PROVINCE_CODE: order.billingProvinceCode,
            BILLING_STATE: order.billingState?.toUpperCase(),
            BILLING_STREET: order.billingStreet,
            BILLING_TAX_ID: order.billingTaxId,
            BILLING_GLN: order.billingGln,
            COD_CURRENCY: order.codCurrency,
            COD_PRICE: toCoreNumber(order.codPrice),
            COD_REF_NUMBER: order.codRefNumber,
            CURRENCY: order.currency,
            ESHOP_ORDER_ID: order.eshopOrderId,
            INVOICE_DATE: order.invoiceDate,
            REQUIRED_EXPEDITION_DATE: order.requiredExpeditionDate,
            INVOICE_NUMBER: order.invoiceNumber,
            MARKETPLACE: order.marketplace,
            MESSAGE: order.message,
            METHOD: 'DEL',
            NOTE: order.note,
            ORDER_PRODUCTS: order.orderProducts
                ? order.orderProducts.map((product) => ({
                      COUNT: toCoreNumber(product.count),
                      PRODUCT_PRICE: toCoreNumber(product.productPrice),
                      LOT: product.lot,
                      LOCATION_ID: product.locationId,
                      PRODUCT_SKU: product.sku,
                      CUSTOMS_PRODUCT_TYPE: product.customsProductType,
                      LINE_STATE: product.lineState || 'E',
                      CUSTOMIZATION_TYPE: product.customizationType || null,
                      CUSTOMIZATION_TEXT: product.customizationText || null,
                      BCX_CODE: product.bcxCodes ? product.bcxCodes.join(',') : null,
                      ESHOP_ORDER_PRODUCT_ID: product.eshopOrderProductId,
                      FILES: product.orderProductFiles
                          ? product.orderProductFiles.map((file) => ({
                                id: file.id,
                                type: file.type,
                                data: file.data,
                                fileType: file.fileType,
                                fileName: file.fileName,
                                fileSize: file.fileSize,
                                ordersProductsId: file.orderProductId
                            }))
                          : undefined
                  }))
                : undefined,
            FILES: order.orderFiles
                ? order.orderFiles.map((file) => ({
                      id: file.id,
                      type: file.type,
                      data: file.data,
                      fileName: file.fileName,
                      fileType: file.fileType,
                      fileSize: file.fileSize || 'unknown'
                  }))
                : undefined,
            ORDER_TYPE: order.orderType,
            PAID: order.paid !== undefined ? Number(order.paid) : undefined,
            PARTNER_ORDER_ID: order.partnerOrderId,
            PAYMENT_METHOD: order.paymentMethod,
            PRICE: toCoreNumber(order.price),
            PRINT_FILES: order.printFiles !== undefined ? Number(order.printFiles) : undefined,
            HOLD_BY_QUALITY: order.holdByQuality !== undefined ? Number(order.holdByQuality) : undefined,
            PICK_FROM_CUSTOMS_WAREHOUSE:
                order.pickFromCustomsWarehouse !== undefined ? Number(order.pickFromCustomsWarehouse) : undefined,
            RECIPIENT_ADDRESS_ADDITIONAL_INFO: order.recipientAddressAdditionalInfo,
            RECIPIENT_ATTENTION_NAME: order.recipientAttentionName,
            RECIPIENT_CITY: order.recipientCity,
            RECIPIENT_COMPANY_ID: order.recipientCompanyId,
            RECIPIENT_COMPANY_NAME: order.recipientCompanyName,
            RECIPIENT_EMAIL: order.recipientEmail,
            RECIPIENT_NAME: order.recipientName,
            RECIPIENT_PHONE: order.recipientPhone,
            RECIPIENT_POST_CODE: order.recipientPostCode,
            RECIPIENT_PROVINCE_CODE: order.recipientProvinceCode,
            RECIPIENT_STATE: order.recipientState?.toUpperCase(),
            RECIPIENT_STREET: order.recipientStreet,
            RECIPIENT_TAX_ID: order.recipientTaxId,
            RECIPIENT_GLN: order.recipientGln,
            SHIPPER: order.shipper,
            ESHOP_NOTE: order.eshopNote,
            EXCHANGE_TRACKING_NUMBER: order.exchangeTrackingNumber,
            SHIPPER_SERVICE_CODE: order.shipperServiceCode,
            BRANCH_ID: order.branchId,
            SHIPPING_PRICE: toCoreNumber(order.shippingPrice),
            STATUS: order.status,
            TRACKING_NUMBER: order.trackingNumbers ? order.trackingNumbers.join(',') : undefined,
            DISABLE_SHIPPING_REORDER:
                order.disableShippingReorder !== undefined ? Number(order.disableShippingReorder) : undefined,
            UNDER_SANCTION: order.underSanction !== undefined ? Number(order.underSanction) : undefined,
            PREORDER_RELEASE_AT: order.preorderReleaseAt,
        };
    }

    public static fromCoreGrid(grid: CoreOrdersGridOutput): GridOutput {
        return {
            data: grid.data
                .map((coreOrder) => OrdersMapper.fromCore(coreOrder))
                .map((order) => CoreGridMapper.omitColumns(order, fromCoreOrderNames, grid.request.columns)),
            request: CoreGridMapper.mapGrid(grid.request, fromCoreOrderNames, 1000),
            totalCount: grid.totalCount
        };
    }

    public static toCoreGrid(grid: GridInput): CoreGridInput {
        return CoreGridMapper.mapGrid(grid, toCoreOrderNames, 1000);
    }
}
